import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import { parseRoles } from '../../utils/rbac';
import Tag from '../shared/Tag';
import Modal from '../shared/Modal';

const PHASES = [
  { id: 'initiation', label: 'Initiation', color: '#8B5CF6', bg: '#F5F3FF' },
  { id: 'planning',   label: 'Planning',   color: '#3B82F6', bg: '#EFF6FF' },
  { id: 'execution',  label: 'Execution',  color: '#10B981', bg: '#ECFDF5' },
  { id: 'close_out',  label: 'Close Out',  color: '#F59E0B', bg: '#FFFBEB' },
];

const TEMPLATE_CATEGORIES = [
  'security', 'documentation', 'testing', 'governance', 'delivery', 'commercial', 'people', 'technical', 'other',
];

export default function RepeatablePage() {
  const { currentUser } = useApp();
  const isAdmin = parseRoles(currentUser?.user_role).includes('admin');
  const [templates, setTemplates]   = useState([]);
  const [teams, setTeams]           = useState([]);
  const [sows, setSows]             = useState([]);
  const [sowTasks, setSowTasks]     = useState({});
  const [expanded, setExpanded]     = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editTpl, setEditTpl]       = useState(null);

  const load = () => {
    Promise.all([
      api.get('repeatable-templates'),
      api.get('sows', { status: 'Active' }),
      api.get('teams').catch(() => []),
    ]).then(async ([t, s, tm]) => {
      setTemplates(t); setSows(s); setTeams(tm || []);
      const map = {};
      for (const sow of s) {
        try { map[sow.id] = await api.get(`sows/${sow.id}/repeatable-tasks`); } catch { map[sow.id] = []; }
      }
      setSowTasks(map);
    });
  };
  useEffect(load, []);

  const deleteTpl = async (id) => {
    if (!confirm('Remove this template? It will be hidden from new SOWs but existing data is kept.')) return;
    await api.delete(`repeatable-templates/${id}`);
    load();
  };

  const getStatusForTemplate = (templateId) => {
    const results = [];
    for (const sow of sows) {
      const tasks = sowTasks[sow.id] || [];
      const match = tasks.find(t => t.template_id === templateId);
      results.push({ sow, status: match?.status || 'not_started', applicable: match?.is_applicable !== 0 });
    }
    return results;
  };

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Repeatable Tasks</h2>
          <div className="text-[9px] text-gray-500">{templates.length} templates · {sows.length} active SOWs</div>
        </div>
        {isAdmin && (
          <button onClick={() => setShowCreate(true)} className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80">
            + New Template
          </button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead><tr>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 w-5"></th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Deliverable</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Category</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Phase</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Team</th>
            <th className="px-2 py-1 text-right text-[8px] uppercase text-green-600 font-bold border-b bg-gray-50">S</th>
            <th className="px-2 py-1 text-right text-[8px] uppercase text-amber-600 font-bold border-b bg-gray-50">M</th>
            <th className="px-2 py-1 text-right text-[8px] uppercase text-red-500 font-bold border-b bg-gray-50">L</th>
            <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">FTE %</th>
            <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Done</th>
            <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">WIP</th>
            <th className="px-2 py-1 text-center text-[8px] uppercase text-red-500 font-bold border-b bg-gray-50">Overdue</th>
            <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Pending</th>
            {isAdmin && <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Actions</th>}
          </tr></thead>
          <tbody>
            {templates.map(t => {
              const statuses   = getStatusForTemplate(t.id);
              const applicable = statuses.filter(s => s.applicable);
              const done       = applicable.filter(s => s.status === 'completed').length;
              const wip        = applicable.filter(s => s.status === 'in_progress').length;
              const overdue    = applicable.filter(s => s.status === 'overdue').length;
              const pending    = applicable.filter(s => s.status === 'not_started').length;
              const isExp      = expanded === t.id;
              const phaseDef   = PHASES.find(p => p.id === (t.phase || 'execution'));
              return (
                <tr key={t.id} className="cursor-pointer hover:bg-gold/5 border-b border-gray-100">
                  <td className="px-2 py-1 text-[9px] text-gray-400" onClick={() => setExpanded(isExp ? null : t.id)}>{isExp ? '▾' : '▸'}</td>
                  <td className="px-2 py-1 text-[10px] font-semibold" onClick={() => setExpanded(isExp ? null : t.id)}>
                    {t.name}
                    {t.description && <div className="text-[8px] text-gray-400 font-normal">{t.description}</div>}
                  </td>
                  <td className="px-2 py-1 text-[9px] text-gray-500" onClick={() => setExpanded(isExp ? null : t.id)}>
                    {t.category ? <span className="text-[7px] px-1.5 py-0.5 rounded font-semibold bg-gray-100 text-gray-600">{t.category}</span> : '—'}
                  </td>
                  <td className="px-2 py-1" onClick={() => setExpanded(isExp ? null : t.id)}>
                    {phaseDef && (
                      <span className="text-[7px] px-1.5 py-0.5 rounded font-semibold" style={{ background: phaseDef.bg, color: phaseDef.color }}>{phaseDef.label}</span>
                    )}
                  </td>
                  <td className="px-2 py-1 text-[9px] text-gray-500" onClick={() => setExpanded(isExp ? null : t.id)}>{t.team_name || '—'}</td>
                  <td className="px-2 py-1 text-[10px] font-semibold text-right text-green-700" onClick={() => setExpanded(isExp ? null : t.id)}>{t.estimated_days_small ?? t.default_estimated_days ?? '—'}d</td>
                  <td className="px-2 py-1 text-[10px] font-semibold text-right text-amber-700" onClick={() => setExpanded(isExp ? null : t.id)}>{t.estimated_days_medium ?? t.default_estimated_days ?? '—'}d</td>
                  <td className="px-2 py-1 text-[10px] font-semibold text-right text-red-600" onClick={() => setExpanded(isExp ? null : t.id)}>{t.estimated_days_large ?? t.default_estimated_days ?? '—'}d</td>
                  <td className="px-2 py-1 text-[9px] text-right text-gray-500" onClick={() => setExpanded(isExp ? null : t.id)}>{t.fte_pct ?? 100}%</td>
                  <td className="px-2 py-1 text-center text-[10px] font-semibold text-emerald-600" onClick={() => setExpanded(isExp ? null : t.id)}>{done}</td>
                  <td className="px-2 py-1 text-center text-[10px] font-semibold text-blue-600"   onClick={() => setExpanded(isExp ? null : t.id)}>{wip}</td>
                  <td className="px-2 py-1 text-center text-[10px] font-bold text-red-600"        onClick={() => setExpanded(isExp ? null : t.id)}>{overdue || '-'}</td>
                  <td className="px-2 py-1 text-center text-[10px] text-gray-500"                 onClick={() => setExpanded(isExp ? null : t.id)}>{pending}</td>
                  {isAdmin && (
                    <td className="px-2 py-1 text-center whitespace-nowrap">
                      <button onClick={e => { e.stopPropagation(); setEditTpl(t); }} className="text-[8px] text-gold hover:underline mr-1">Edit</button>
                      <button onClick={e => { e.stopPropagation(); deleteTpl(t.id); }} className="text-[8px] text-red-400 hover:text-red-600">Remove</button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Expanded SOW breakdown */}
      {expanded && (() => {
        const statuses = getStatusForTemplate(expanded).filter(s => s.applicable);
        return (
          <div className="bg-gray-50 border border-gray-200 rounded mt-2 p-3 fade-in">
            <div className="text-[9px] font-semibold text-gray-600 mb-2">SOW breakdown for: {templates.find(t => t.id === expanded)?.name}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {statuses.map(s => (
                <div key={s.sow.id} className="flex items-center gap-2 bg-white rounded border border-gray-200 px-2 py-1.5">
                  <Tag status={s.status} />
                  <span className="text-[9px] font-semibold">{s.sow.reference}</span>
                </div>
              ))}
              {statuses.length === 0 && <div className="col-span-4 text-[9px] text-gray-400">Not applicable to any active SOW</div>}
            </div>
          </div>
        );
      })()}

      {/* Create Modal */}
      {showCreate && <TemplateModal teams={teams} onClose={() => setShowCreate(false)} onSave={async data => { await api.post('repeatable-templates', data); setShowCreate(false); load(); }} />}

      {/* Edit Modal */}
      {editTpl && <TemplateModal template={editTpl} teams={teams} onClose={() => setEditTpl(null)} onSave={async data => { await api.put(`repeatable-templates/${editTpl.id}`, data); setEditTpl(null); load(); }} />}
    </div>
  );
}

function TemplateModal({ template, teams, onClose, onSave }) {
  const [form, setForm] = useState({
    name:                    template?.name || '',
    description:             template?.description || '',
    category:                template?.category || 'delivery',
    estimated_days_small:    template?.estimated_days_small  ?? template?.default_estimated_days ?? 1,
    estimated_days_medium:   template?.estimated_days_medium ?? template?.default_estimated_days ?? 2,
    estimated_days_large:    template?.estimated_days_large  ?? template?.default_estimated_days ?? 3,
    default_band_code:       template?.default_band_code || 'B5',
    team_id:                 template?.team_id || '',
    phase:                   template?.phase || 'execution',
    fte_pct:                 template?.fte_pct ?? 100,
    checklist_items:         (() => { try { return template?.checklist_items ? JSON.parse(template.checklist_items) : []; } catch (_) { return []; } })(),
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = () => {
    const s = parseFloat(form.estimated_days_small)  || 1;
    const m = parseFloat(form.estimated_days_medium) || 2;
    const l = parseFloat(form.estimated_days_large)  || 3;
    const items = form.checklist_items.filter(x => x.trim());
    onSave({
      ...form,
      category: form.category || 'delivery',
      estimated_days_small:  s,
      estimated_days_medium: m,
      estimated_days_large:  l,
      default_estimated_days: m, // keep legacy field in sync with medium
      fte_pct: parseInt(form.fte_pct) || 100,
      team_id: form.team_id || null,
      checklist_items: items.length > 0 ? JSON.stringify(items) : null,
    });
  };

  return (
    <Modal title={template ? 'Edit Repeatable Task' : 'New Repeatable Task Template'} onClose={onClose} footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handleSave} disabled={!form.name.trim()}
          className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">Save</button></>
    }>
      <div className="grid grid-cols-2 gap-2">
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name *</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description</label>
          <textarea className="form-textarea" rows="2" value={form.description} onChange={e => set('description', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Checklist Steps</label>
          <div className="space-y-1">
            {form.checklist_items.map((item, i) => (
              <div key={i} className="flex gap-1 items-center">
                <span className="text-[8px] text-gray-400 w-4 text-right flex-shrink-0">{i + 1}.</span>
                <input
                  className="form-input flex-1 text-[9px]"
                  value={item}
                  onChange={e => { const arr = [...form.checklist_items]; arr[i] = e.target.value; set('checklist_items', arr); }}
                  placeholder={`Step ${i + 1}`}
                />
                <button
                  onClick={() => set('checklist_items', form.checklist_items.filter((_, j) => j !== i))}
                  className="text-[10px] text-red-400 hover:text-red-600 px-1 flex-shrink-0">✕</button>
              </div>
            ))}
            <button
              onClick={() => set('checklist_items', [...form.checklist_items, ''])}
              className="text-[9px] text-blue-600 hover:text-blue-700 font-semibold mt-0.5">+ Add step</button>
          </div>
          <div className="text-[7.5px] text-gray-400 mt-1">Steps shown as a checklist when this template is linked to a plan task</div>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Category</label>
          <select className="form-select" value={form.category} onChange={e => set('category', e.target.value)}>
            {TEMPLATE_CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">SOW Phase</label>
          <select className="form-select" value={form.phase} onChange={e => set('phase', e.target.value)}>
            {PHASES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Responsible Team</label>
          <select className="form-select" value={form.team_id} onChange={e => set('team_id', e.target.value)}>
            <option value="">— no default —</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        {/* S / M / L estimated days */}
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Est. Days by Complexity</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label className="block text-[8px] font-semibold text-green-700 mb-0.5">Small</label>
              <input className="form-input" type="number" min="0.5" step="0.5" value={form.estimated_days_small} onChange={e => set('estimated_days_small', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-semibold text-amber-700 mb-0.5">Medium</label>
              <input className="form-input" type="number" min="0.5" step="0.5" value={form.estimated_days_medium} onChange={e => set('estimated_days_medium', e.target.value)} />
            </div>
            <div>
              <label className="block text-[8px] font-semibold text-red-600 mb-0.5">Large</label>
              <input className="form-input" type="number" min="0.5" step="0.5" value={form.estimated_days_large} onChange={e => set('estimated_days_large', e.target.value)} />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">FTE %</label>
          <input className="form-input" type="number" min="10" max="200" step="10" value={form.fte_pct} onChange={e => set('fte_pct', e.target.value)} placeholder="100" />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Default Band</label>
          <select className="form-select" value={form.default_band_code} onChange={e => set('default_band_code', e.target.value)}>
            {['B1','B2','B3','B4','B5','B6','SP2','SP3'].map(b => <option key={b}>{b}</option>)}
          </select>
        </div>
      </div>
    </Modal>
  );
}
